<template>
  <div class="animated fadeIn">
    <div class="card shadow-sm border-0">
      <div class="card-header bg-dark text-white d-flex align-items-center justify-content-between py-2">
        <h6 class="mb-0"><font-awesome-icon icon="list" /> Office Payroll Draft</h6>
        <span>
          <b-badge variant="dark" class="mr-2" v-if="payroll_group">{{ payroll_group.name }}</b-badge>
          <b-badge variant="info">{{ form.date_from }}</b-badge> to
          <b-badge variant="info">{{ form.date_to }}</b-badge>
        </span>
      </div>
      <div class="card-body position-relative">
        <b-row align-v="center" class="loader" v-if="loading">
          <b-col class="text-center"><b-spinner></b-spinner><div class="mt-2"><strong>Loading</strong></div></b-col>
        </b-row>

        <b-row class="mb-3">
          <b-col md="4">
            <label class="small font-weight-bold text-uppercase">Voucher Number</label>
            <b-form-input v-model="form.voucher_number" size="sm"></b-form-input>
          </b-col>
          <b-col md="4">
            <label class="small font-weight-bold text-uppercase">Check Number</label>
            <b-form-input v-model="form.check_number" size="sm"></b-form-input>
          </b-col>
          <b-col md="4">
            <label class="small font-weight-bold text-uppercase">Remarks</label>
            <b-form-input v-model="form.remarks" size="sm"></b-form-input>
          </b-col>
        </b-row>

        <div class="table-responsive">
          <table class="table table-bordered table-sm">
            <thead class="thead-light text-center">
              <tr>
                <th rowspan="2" class="align-middle text-left">Employee</th>
                <th colspan="3">Hours</th>
                <th rowspan="2" class="align-middle">Days</th>
                <th rowspan="2" class="align-middle text-right">Holiday Pay</th>
                <th rowspan="2" class="align-middle text-right">Cost</th>
                <th rowspan="2" class="align-middle text-right">CA Balance</th>
                <th colspan="5">Deductions</th>
                <th rowspan="2" class="align-middle text-right">Total Payout</th>
                <th rowspan="2" class="align-middle text-center">Include</th>
              </tr>
              <tr>
                <th>Reg</th><th>OT</th><th>ND</th>
                <th>CA</th><th>SSS</th><th>PhilHealth</th><th>Pag-IBIG</th><th>Others</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(d, i) in form.payroll_details" :key="i" :class="{ 'text-muted bg-light': !d.include }">
                <td class="text-nowrap">{{ d.full_name }}</td>
                <td class="text-center">{{ effReg(d) }}</td>
                <td class="text-center">{{ effOt(d) }}</td>
                <td class="text-center">{{ effNd(d) }}</td>
                <td class="text-center">
                  <a v-if="d.day_items && d.day_items.length" href="#" @click.prevent="openDays(d)">
                    {{ includedDays(d) }} <font-awesome-icon icon="edit" class="text-muted" />
                  </a>
                  <span v-else>{{ includedDays(d) }}</span>
                  <b-badge v-if="includedDays(d) == 0 && holidayPay(d) > 0" variant="success">holiday</b-badge>
                </td>
                <td class="text-right">
                  <a v-if="d.holiday_items && d.holiday_items.length" href="#" @click.prevent="openHolidays(d)">
                    {{ money(holidayPay(d)) }}
                    <font-awesome-icon icon="edit" class="text-muted" />
                  </a>
                  <span v-else>{{ money(holidayPay(d)) }}</span>
                </td>
                <td class="text-right">{{ money(effectiveGross(d)) }}</td>
                <td class="text-right">{{ money(d.previous_ca_balance) }}</td>
                <td><b-form-input type="number" size="sm" v-model.number="d.ca_payment" style="min-width:80px"></b-form-input></td>
                <td><b-form-input type="number" size="sm" v-model.number="d.sss_payment" style="min-width:80px"></b-form-input></td>
                <td><b-form-input type="number" size="sm" v-model.number="d.philhealth_payment" style="min-width:80px"></b-form-input></td>
                <td><b-form-input type="number" size="sm" v-model.number="d.pagibig_payment" style="min-width:80px"></b-form-input></td>
                <td><b-form-input type="number" size="sm" v-model.number="d.other_payment" style="min-width:80px"></b-form-input></td>
                <td class="text-right font-weight-bold">{{ money(totalPayout(d)) }}</td>
                <td class="text-center">
                  <b-form-checkbox v-model="d.include" switch></b-form-checkbox>
                </td>
              </tr>
              <tr v-if="form.payroll_details.length === 0 && !loading">
                <td colspan="15" class="text-center text-muted">No approved, unpaid attendance found for this period.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="text-right">
          <b-button variant="secondary" size="sm" class="mr-2" @click="$router.push('/manpowers/office-payroll')">Cancel</b-button>
          <b-button variant="success" size="sm" :disabled="form.payroll_details.length === 0" @click="authModal = true">
            <font-awesome-icon icon="save" /> Save Payroll
          </b-button>
        </div>
      </div>
    </div>

    <b-modal v-model="authModal" centered size="sm" :hide-footer="true" title="Authorization">
      <b-form @submit.prevent="save">
        <b-form-group label="Username">
          <b-form-input v-model="authorization.username" required autocomplete="off"></b-form-input>
        </b-form-group>
        <b-form-group label="Password">
          <b-form-input type="password" v-model="authorization.password" required autocomplete="off"></b-form-input>
        </b-form-group>
        <b-button type="submit" variant="success" block><font-awesome-icon icon="save" /> Authorize & Save</b-button>
      </b-form>
    </b-modal>

    <b-modal v-model="daysModal" centered size="lg" :hide-footer="true" :title="daysEmployee ? ('Days Rendered — ' + daysEmployee.full_name) : 'Days Rendered'">
      <p class="small text-muted">Uncheck a day to exclude it from this payroll (its attendance stays available for a later run).</p>
      <table class="table table-sm table-bordered" v-if="daysEmployee">
        <thead class="thead-light">
          <tr><th>Date</th><th>Type</th><th class="text-center">Reg</th><th class="text-center">OT</th><th class="text-center">ND</th><th class="text-right">Day Total</th><th class="text-center">Include</th></tr>
        </thead>
        <tbody>
          <tr v-for="(dy, di) in daysEmployee.day_items" :key="di" :class="{ 'text-muted': !dy.include }">
            <td>{{ dy.formatted_date.other_formats.format_1 }}</td>
            <td>
              <b-badge v-if="dy.holiday_type == 1" variant="success">Reg Holiday</b-badge>
              <b-badge v-else-if="dy.holiday_type == 2" variant="warning">Spec Holiday</b-badge>
              <span v-else>Ordinary</span>
            </td>
            <td class="text-center">{{ dy.worked_hours }}</td>
            <td class="text-center">{{ dy.ot_hours }}</td>
            <td class="text-center">{{ dy.nd_hours }}</td>
            <td class="text-right">{{ money(dy.total) }}</td>
            <td class="text-center"><b-form-checkbox v-model="dy.include" switch></b-form-checkbox></td>
          </tr>
        </tbody>
      </table>
      <div class="text-right">
        <b-button variant="secondary" size="sm" @click="daysModal = false">Done</b-button>
      </div>
    </b-modal>

    <b-modal v-model="holidayModal" centered size="md" :hide-footer="true" :title="holidayEmployee ? ('Holidays — ' + holidayEmployee.full_name) : 'Holidays'">
      <p class="small text-muted">Uncheck a holiday to exclude its pay for this employee.</p>
      <table class="table table-sm table-bordered" v-if="holidayEmployee">
        <thead class="thead-light">
          <tr><th>Date</th><th>Type</th><th>Status</th><th class="text-right">Amount</th><th class="text-center">Include</th></tr>
        </thead>
        <tbody>
          <tr v-for="(h, hi) in holidayEmployee.holiday_items" :key="hi" :class="{ 'text-muted': !h.include }">
            <td>{{ h.formatted_date.other_formats.format_1 }}</td>
            <td><b-badge :variant="h.type == 1 ? 'success' : 'warning'">{{ h.type == 1 ? 'Regular' : 'Special' }}</b-badge></td>
            <td>{{ h.worked ? 'Worked' : 'Unworked' }}</td>
            <td class="text-right">{{ money(h.amount) }}</td>
            <td class="text-center"><b-form-checkbox v-model="h.include" switch></b-form-checkbox></td>
          </tr>
        </tbody>
      </table>
      <div class="text-right">
        <b-button variant="secondary" size="sm" @click="holidayModal = false">Done</b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
  import SwalMixin from '@/views/mixins/Swal.js'

  export default {
    name: 'OfficePayrollDraft',
    mixins: [SwalMixin],
    data() {
      return {
        loading: true,
        authModal: false,
        holidayModal: false,
        holidayEmployee: null,
        daysModal: false,
        daysEmployee: null,
        authorization: { username: '', password: '' },
        payroll_group: null,
        form: {
          payroll_group_id: null,
          date_from: '',
          date_to: '',
          voucher_number: '',
          check_number: '',
          remarks: '',
          payroll_details: [],
          office_time_log_ids: [],
        }
      }
    },
    created() {
      this.form.payroll_group_id = this.$route.params.payroll_group_id;
      this.form.date_from = this.$route.params.date_from;
      this.form.date_to = this.$route.params.date_to;
      this.generate();
    },
    watch: {
      // Auto-exclude an employee whose total payout drops to zero (or below).
      'form.payroll_details': {
        deep: true,
        handler(rows) {
          rows.forEach(d => {
            if (d.include && this.totalPayout(d) <= 0) {
              d.include = false;
            }
          });
        }
      }
    },
    methods: {
      money(value) {
        const n = Number(value);
        if (isNaN(n)) return value;
        return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      },
      incDays(d) {
        return (d.day_items || []).filter(x => x.include);
      },
      incHols(d) {
        return (d.holiday_items || []).filter(x => x.include);
      },
      includedDays(d) {
        return this.incDays(d).length;
      },
      effReg(d) {
        return this.round2(this.incDays(d).reduce((t, x) => t + Number(x.worked_hours || 0), 0));
      },
      effOt(d) {
        return this.round2(this.incDays(d).reduce((t, x) => t + Number(x.ot_hours || 0), 0));
      },
      effNd(d) {
        return this.round2(this.incDays(d).reduce((t, x) => t + Number(x.nd_hours || 0), 0));
      },
      holidayPay(d) {
        // Sum of INCLUDED holiday items (worked premiums + unworked full pay).
        if (d.holiday_items && d.holiday_items.length) {
          return this.incHols(d).reduce((t, x) => t + Number(x.amount || 0), 0);
        }
        return Number(d.regular_holiday_amount || 0) + Number(d.special_holiday_amount || 0);
      },
      effectiveGross(d) {
        if ((d.day_items && d.day_items.length) || (d.holiday_items && d.holiday_items.length)) {
          const duty = this.incDays(d).reduce((t, x) => t + Number(x.total || 0), 0); // base + OT + ND
          return duty + this.holidayPay(d);
        }
        return Number(d.initial_payout || 0);
      },
      round2(n) {
        return Math.round(Number(n) * 100) / 100;
      },
      openDays(d) {
        this.daysEmployee = d;
        this.daysModal = true;
      },
      openHolidays(d) {
        this.holidayEmployee = d;
        this.holidayModal = true;
      },
      totalPayout(d) {
        const deductions = Number(d.ca_payment || 0) + Number(d.sss_payment || 0) + Number(d.philhealth_payment || 0) + Number(d.pagibig_payment || 0) + Number(d.other_payment || 0);
        return this.round2(this.effectiveGross(d) - deductions);
      },
      // Recompute all stored amounts/hours for an employee from included days + unworked holidays.
      buildDetail(d) {
        const days = this.incDays(d);
        const hols = this.incHols(d);
        const sum = (arr, f) => arr.reduce((t, x) => t + Number(f(x) || 0), 0);
        const holsOf = (type) => hols.filter(h => h.type === type);
        return Object.assign({}, d, {
          total_days: days.length,
          total_regular_hours: this.round2(sum(days, x => x.regular_hours)),
          total_ot_hours: this.round2(sum(days, x => x.ot_hours)),
          total_nd_hours: this.round2(sum(days, x => x.nd_hours)),
          basic_amount: this.round2(sum(days, x => x.basic_amount)),
          ot_amount: this.round2(sum(days, x => x.ot_amount)),
          nd_amount: this.round2(sum(days, x => x.nd_amount)),
          total_regular_holiday_hours: this.round2(sum(holsOf(1), x => x.hours)),
          regular_holiday_amount: this.round2(sum(holsOf(1), x => x.amount)),
          total_special_holiday_hours: this.round2(sum(holsOf(2), x => x.hours)),
          special_holiday_amount: this.round2(sum(holsOf(2), x => x.amount)),
          initial_payout: this.round2(this.effectiveGross(d)),
          total_payout: this.totalPayout(d),
        });
      },
      generate() {
        this.loading = true;
        this.axios.get('office-payroll/generate', { params: { payroll_group_id: this.form.payroll_group_id, date_from: this.form.date_from, date_to: this.form.date_to } })
          .then(response => {
            const r = response.data;
            this.payroll_group = r.payroll_group || null;
            this.form.office_time_log_ids = r.office_time_log_ids;
            this.form.payroll_details = r.payroll_draft.map(row => Object.assign({}, row, {
              ca_payment: 0,
              sss_payment: 0,
              philhealth_payment: 0,
              pagibig_payment: 0,
              other_payment: 0,
              remarks: '',
              include: true,
            }));
            this.loading = false;
          }).catch(error => {
            this.loading = false;
            this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
            this.swalRequestError();
          });
      },
      save() {
        // Only save included employees, and only lock the timelogs of the
        // employees actually being paid (excluded ones stay available).
        const included = this.form.payroll_details.filter(d => d.include);
        if (included.length === 0) {
          this.authModal = false;
          this.swal_transaction_error_data.text = 'Please include at least one employee.';
          this.swalRequestError();
          return;
        }
        // Duty and holiday are marked paid independently: a day's DUTY log is
        // locked via office_payroll_id, a worked holiday via holiday_payroll_id.
        let dutyLogIds = [];
        let holidayLogIds = [];
        included.forEach(d => {
          this.incDays(d).forEach(x => {
            if (x.office_time_log_id) dutyLogIds.push({ id: x.office_time_log_id });
          });
          this.incHols(d).forEach(h => {
            if (h.worked && h.office_time_log_id) holidayLogIds.push({ id: h.office_time_log_id });
          });
        });
        const payload = {
          payroll_group_id: this.form.payroll_group_id,
          date_from: this.form.date_from,
          date_to: this.form.date_to,
          voucher_number: this.form.voucher_number,
          check_number: this.form.check_number,
          remarks: this.form.remarks,
          authorization: this.authorization,
          duty_log_ids: dutyLogIds,
          holiday_log_ids: holidayLogIds,
          payroll_details: included.map(d => this.buildDetail(d)),
        };
        this.loading = true;
        this.axios.post('office-payroll/save', payload).then(response => {
          this.authModal = false;
          this.swalTransactionCompleted();
          this.$router.push('/manpowers/office-payroll/' + response.data.id);
        }).catch(error => {
          this.loading = false;
          this.swal_transaction_error_data.text = (error.response && error.response.data) ? error.response.data : 'Something went wrong.';
          this.swalRequestError();
        });
      }
    }
  }
</script>
